'use client';

import React, { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import { SENSOR_FUSION_DATA_LAYERS } from '@/types/shared/navigation-tabs.enums';
import { useAppFilters } from '@/hooks';
import { useParams } from 'react-router-dom';
import { useActiveAssetsType } from '@/hooks/shared/useActiveAssetsType';
import { useSelector } from 'react-redux';
import type { RootState } from '@/store/store';
import { extractGraphConfigurations } from '@/modules/AssetsStudioPage/SensorFusion/utils/initialize-graphs';
import { useManagedGraphs } from '@/modules/AssetsStudioPage/SensorFusion/hooks';
import { useSensorQueryParams, useEnhancedQueryParams } from '@/modules/AssetsStudioPage/SensorFusion/hooks/use-sensor-query-params';
import { useVerticalLines } from '@/hooks/shared/DataLayers/use-vertical-lines';
import {
  createWaveformDataLayersConfig,
  WAVEFORM_DATA_LAYERS_RENDER_ORDER,
} from './config/data-layers-config';
import {
  createInitializationKey,
  initializeEnabledGraphs,
} from '@/components/pages/AssetsStudioPage/MainContent/SensorData/Spectrum/utils/initialize-graphs';
import { isExpandableLegendEnabled } from '@/config/featureFlags';
import type { ExpandableLegend, DataLayerWithExpandableLegend } from '@/types/ui/expandable-legend.types';
import { mergeExpandableLegendItemsWithVerticalLines, convertItemsToPopoverFormat } from '@/utils/expandableLegend.utils';

// Helper to compare two Sets by contents
const areSetsEqual = (a: Set<string> | undefined, b: Set<string> | undefined): boolean => {
  if (a === b) return true;
  if (!a || !b) return false;
  if (a.size !== b.size) return false;
  for (const item of a) {
    if (!b.has(item)) return false;
  }
  return true;
};

interface WaveformProps {
  asset: any;
  components: any[];
  diagnostics: any[];
  config: any;
  activeOperationalModes: string[];
  activeDataLayers: string[];
  onLoadingStateChange?: (isLoading: boolean) => void;
  triggerRefresh?: number;
  // Shared with Spectrum for timestamp synchronization
  sensorFusionVerticalLines?: any;
  onRemoveSensorFusionMarker?: (markerId: string) => void;
  onClearAllSensorFusionMarkers?: () => void;
  selectedMarkerTimestamp?: string | null;
  setSelectedMarkerTimestamp?: (timestamp: string | null) => void;
  selectedTimestampsSet?: Set<string>;
  setSelectedTimestampsSet?: (timestamps: Set<string> | ((prev: Set<string>) => Set<string>)) => void;
  masterSensorColorMap?: Record<string, string>;
  spectrumVerticalLinesState?: ReturnType<typeof useVerticalLines>;
  allowMultiTimestampMode?: boolean;
}

/**
 * Waveform - Main container component for the Waveform inner-tab
 * Follows the same pattern as NewSpectrum - uses ManagedGraphsSection for all data layers
 */
export const Waveform: React.FC<WaveformProps> = ({
  asset,
  components,
  diagnostics,
  config,
  activeOperationalModes,
  activeDataLayers,
  onLoadingStateChange,
  triggerRefresh = 0,
  sensorFusionVerticalLines,
  onRemoveSensorFusionMarker,
  onClearAllSensorFusionMarkers,
  selectedMarkerTimestamp,
  setSelectedMarkerTimestamp,
  selectedTimestampsSet,
  setSelectedTimestampsSet,
  masterSensorColorMap,
  spectrumVerticalLinesState,
  allowMultiTimestampMode = true,
}) => {
  const { isMobileAsset } = useActiveAssetsType();
  const { siteId, id: assetId } = useParams();
  const assetName = asset?.asset_name;
  const { selectedComponent: selectedComponentId, selectedPart } = useAppFilters();
  const {
    dateRange: { from, to },
  } = useSelector((state: RootState) => state.assetsStudio);
  const { selectedComponentObj } = useSelector((state: RootState) => state.assetsStudio);

  const [forceDataRefresh, setForceDataRefresh] = useState(0);
  const [filterRefreshKey, setFilterRefreshKey] = useState(0);

  // Loading sensors state
  const [loadingSensors, setLoadingSensors] = useState<Set<string>>(new Set());

  // Debounce timer ref
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const selectedTimestampsSetRef = useRef<Set<string> | undefined>(selectedTimestampsSet);
  selectedTimestampsSetRef.current = selectedTimestampsSet;

  // Last fetched timestamp ref
  const lastFetchedTimestampRef = useRef<string | string[] | undefined>(undefined);

  // Vertical lines state (local fallback if not provided)
  const localVerticalLinesState = useVerticalLines();
  const verticalLinesState = spectrumVerticalLinesState || localVerticalLinesState;

  // Extract graph configurations from config
  const baseWaveformGraphs = useMemo(() => {
    const extracted = extractGraphConfigurations(config);
    return extracted.waveformGraphs || [];
  }, [config, selectedComponentId]);

  // Get timestamps from sensorFusionVerticalLines to use as features
  const timestampFeatures = useMemo(() => {
    const verticalLines = sensorFusionVerticalLines?.verticalLines || [];
    return verticalLines
      .filter((line: any) => line.timestamp || line.xValue)
      .map((line: any, index: number) => ({
        id: line.id || `ts-${index}`,
        label: line.label || `Timestamp ${index + 1}`,
        timestamp: line.timestamp || line.xValue,
        isDefault: index === 0, // First timestamp is default
      }));
  }, [sensorFusionVerticalLines?.verticalLines]);

  // Inject timestamp features into graph config
  const waveformGraphs = useMemo(() => {
    if (timestampFeatures.length === 0) {
      return baseWaveformGraphs;
    }
    return baseWaveformGraphs.map((graph: any) => ({
      ...graph,
      features: timestampFeatures,
    }));
  }, [baseWaveformGraphs, timestampFeatures]);

  // Get selected component and part info
  const selectedComponent = useMemo(
    () => components?.find((component) => component?.component_id === selectedComponentId),
    [components, selectedComponentId]
  );

  const selectedPartObj = selectedComponent?.subcomponents?.find(
    (el: any) => el.subcomponent_id === selectedPart
  );

  const baseTitle = selectedPartObj
    ? selectedPartObj?.subcomponent_ui_name
    : selectedComponent?.component_ui_name || selectedComponent?.component_name;

  // Initialize enabled graphs
  const initializationKeyRef = useRef<string>('');

  const initialEnabled = useMemo(() => {
    const result = initializeEnabledGraphs(waveformGraphs);
    const initKey = createInitializationKey(selectedComponentId, selectedPart, waveformGraphs);

    if (initKey !== initializationKeyRef.current) {
      initializationKeyRef.current = initKey;
    }

    return result;
  }, [waveformGraphs, selectedComponentId, selectedPart]);

  // Base query params for API calls
  const baseQueryParams = useSensorQueryParams({
    siteId,
    assetId,
    assetName,
    selectedComponentId,
    selectedPart,
    isMobileAsset,
    from,
    to,
    isFullPull: false,
  });

  // Waveform manager
  const waveformManager = useManagedGraphs({
    graphs: waveformGraphs,
    initialEnabled,
    dataLayerKey: SENSOR_FUSION_DATA_LAYERS.WAVEFORM,
  });

  // Legend sync mode
  const [legendSyncMode, setLegendSyncMode] = useState<'multi' | 'focus'>('multi');
  const [focusedSensors, setFocusedSensors] = useState<Record<string, string>>({});
  const savedEnabledSensorsRef = useRef<Record<string, Record<string, boolean>> | null>(null);

  const legendSync = useMemo(() => ({
    mode: legendSyncMode,
    toggleMode: () => {
      setLegendSyncMode(prev => {
        const newMode = prev === 'multi' ? 'focus' : 'multi';
        if (newMode === 'multi') {
          setFocusedSensors({});
        }
        return newMode;
      });
    },
    getSavedState: () => savedEnabledSensorsRef.current,
    clearSavedState: () => { savedEnabledSensorsRef.current = null; },
  }), [legendSyncMode]);

  // Focus handler
  const handleFocus = useCallback((sensorName: string, sectionId?: string, currentEnabledSensors?: Record<string, Record<string, boolean>>) => {
    if (!sectionId) return;

    const focusedInAnySection = Object.entries(focusedSensors).find(([_, name]) => name === sensorName);
    const isTogglingOff = focusedInAnySection !== undefined;

    if (isTogglingOff) {
      const newFocusedSensors = { ...focusedSensors };
      if (focusedInAnySection) {
        const [actualSectionId] = focusedInAnySection;
        delete newFocusedSensors[actualSectionId];
      }
      setFocusedSensors(newFocusedSensors);
      if (Object.keys(newFocusedSensors).length === 0) {
        setLegendSyncMode('multi');
      }
      return;
    }

    if (currentEnabledSensors) {
      savedEnabledSensorsRef.current = JSON.parse(JSON.stringify(currentEnabledSensors));
    }

    setFocusedSensors(prev => ({
      ...prev,
      [sectionId]: sensorName,
    }));
    setLegendSyncMode('focus');
  }, [focusedSensors]);

  const focusMode = legendSyncMode === 'focus';

  // Sensor data registration
  const [registeredSensorData, setRegisteredSensorData] = useState<{
    enabledSensors: Record<string, Record<string, boolean>>;
    sensorColors: Record<string, Record<string, string>>;
  }>({
    enabledSensors: {},
    sensorColors: {},
  });

  const registerSensorData = useCallback(
    (dataLayer: string, enabledSensors: Record<string, boolean>, sensorColors: Record<string, string>) => {
      setRegisteredSensorData((prev) => ({
        enabledSensors: {
          ...prev.enabledSensors,
          [dataLayer]: enabledSensors,
        },
        sensorColors: {
          ...prev.sensorColors,
          [dataLayer]: sensorColors,
        },
      }));
    },
    []
  );

  // Toggle sensor handler
  const handleToggleSensor = useCallback(
    (sectionId: string, sensorName: string) => {
      // Standard toggle behavior
    },
    []
  );

  // Loading state change handler
  const handleSensorLoadingStateChange = useCallback(
    (isLoading: boolean) => {
      onLoadingStateChange?.(isLoading);
    },
    [onLoadingStateChange]
  );

  // Reset handlers
  const [triggerSensorReset, setTriggerSensorReset] = useState(false);

  // Debounced selected timestamps
  const [debouncedSelectedTimestampsSet, setDebouncedSelectedTimestampsSet] = useState<Set<string> | undefined>(
    selectedTimestampsSet
  );

  // Visible timestamps state
  const [visibleTimestampsSet, setVisibleTimestampsSet] = useState<Set<string>>(new Set());

  // Sync visible with selected
  useEffect(() => {
    if (!selectedTimestampsSet) return;

    setVisibleTimestampsSet((prev) => {
      const newSet = new Set(prev);
      let changed = false;

      selectedTimestampsSet.forEach((timestampId) => {
        if (!newSet.has(timestampId)) {
          newSet.add(timestampId);
          changed = true;
        }
      });

      Array.from(newSet).forEach((timestampId) => {
        if (!selectedTimestampsSet.has(timestampId)) {
          newSet.delete(timestampId);
          changed = true;
        }
      });

      return changed ? newSet : prev;
    });
  }, [selectedTimestampsSet]);

  // Debounce selectedTimestampsSet updates
  useEffect(() => {
    const currentSetSize = selectedTimestampsSet?.size || 0;

    if (legendSyncMode !== 'focus' && currentSetSize <= 1) {
      setDebouncedSelectedTimestampsSet((prev) => {
        const newSet = new Set(selectedTimestampsSet || []);
        if (areSetsEqual(prev, newSet)) return prev;
        return newSet;
      });
      return;
    }

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      setDebouncedSelectedTimestampsSet((prev) => {
        const newSet = new Set(selectedTimestampsSetRef.current || []);
        if (areSetsEqual(prev, newSet)) return prev;
        return newSet;
      });
    }, 200);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [selectedTimestampsSet, legendSyncMode]);

  // Timestamp param for API
  const timestampParam = useMemo(() => {
    if (selectedTimestampsSet && selectedTimestampsSet.size > 0) {
      const allTimestamps = Array.from(selectedTimestampsSet);
      return allTimestamps.length === 1 ? allTimestamps[0] : allTimestamps;
    }
    if (selectedMarkerTimestamp) {
      return selectedMarkerTimestamp;
    }
    return undefined;
  }, [selectedTimestampsSet, selectedMarkerTimestamp]);

  // Force refresh when timestamp changes
  useEffect(() => {
    if (timestampParam === undefined) return;

    const currentTimestampKey = Array.isArray(timestampParam)
      ? [...timestampParam].sort().join(',')
      : timestampParam;
    const lastTimestampKey = Array.isArray(lastFetchedTimestampRef.current)
      ? [...lastFetchedTimestampRef.current].sort().join(',')
      : lastFetchedTimestampRef.current;

    if (currentTimestampKey === lastTimestampKey) return;

    lastFetchedTimestampRef.current = timestampParam;
    setForceDataRefresh(prev => prev + 1);
  }, [timestampParam]);

  // Additional params
  const additionalParams = useMemo(() => {
    const params: Record<string, any> = {
      data_layer: SENSOR_FUSION_DATA_LAYERS.WAVEFORM,
    };
    if (timestampParam !== undefined) {
      params.snapshot_timestamp = timestampParam;
    }
    return params;
  }, [timestampParam]);

  const enhancedQueryParams = useEnhancedQueryParams(baseQueryParams, additionalParams);

  // Get expandable legend from config
  const expandableLegend: ExpandableLegend | undefined = useMemo(() => {
    // First check direct expandableLegend on config
    if (config?.expandableLegend?.enabled) {
      return config.expandableLegend;
    }

    // Then check for waveform data layer with expandable legend
    const waveformDataLayer = config?.dataLayers?.find(
      (layer: any) => layer.id === SENSOR_FUSION_DATA_LAYERS.WAVEFORM
    );
    if (waveformDataLayer?.expandableLegend?.enabled) {
      return waveformDataLayer.expandableLegend;
    }

    // Check any data layer with expandable legend
    const anyDataLayerWithLegend = config?.dataLayers?.find(
      (layer: any) => layer.expandableLegend?.enabled
    );
    if (anyDataLayerWithLegend?.expandableLegend) {
      return anyDataLayerWithLegend.expandableLegend;
    }

    return undefined;
  }, [config]);

  // All timestamp items (backend + user markers merged)
  const allTimestampItems = useMemo(() => {
    const backendItems = expandableLegend?.items || [];
    const userMarkerLines = sensorFusionVerticalLines?.verticalLines || [];

    if (!expandableLegend) {
      return backendItems;
    }

    // Merge backend items with user-added markers from SensorFusion
    return mergeExpandableLegendItemsWithVerticalLines(
      backendItems,
      userMarkerLines,
      expandableLegend
    );
  }, [expandableLegend, sensorFusionVerticalLines?.verticalLines]);

  // State for selected timestamp item IDs (which timestamps are selected in the legend)
  const [selectedItemIds, setSelectedItemIds] = useState<Set<string>>(() => {
    if (isExpandableLegendEnabled() && expandableLegend) {
      return new Set(expandableLegend.defaultSelection || []);
    }
    return new Set();
  });

  // State for enabled series (acceleration/velocity)
  const [enabledSeries, setEnabledSeries] = useState<Set<string>>(
    new Set(['acceleration', 'velocity']) // Both enabled by default
  );

  // Fixed colors for Acceleration and Velocity - using timestamp palette (same as spectrum)
  const ACCELERATION_COLOR = '#5ed3f3'; // Light cyan (rawColorPalette index 0)
  const VELOCITY_COLOR = '#6667dd'; // Purple (rawColorPalette index 1)

  // Generate expandableLegendSeries for timestamps as primary items with Acceleration/Velocity as sub-items
  // NEW Structure: [{ id: timestampId, label: timestampLabel, subSeries: [accel, velocity] }, ...]
  // Each timestamp has 2 separate waveform files - one for acceleration, one for velocity
  const waveformExpandableLegendSeries = useMemo(() => {
    if (!expandableLegend?.enabled || allTimestampItems.length === 0) {
      return [];
    }

    // Create one entry per timestamp with Acceleration/Velocity as sub-items
    // Use fixed colors for consistency
    return allTimestampItems.map((item: any) => {
      const isSelected = selectedItemIds.has(item.id);

      return {
        id: item.id,
        label: item.label, // Timestamp label (e.g., "08:00 (5s)")
        color: item.color || '#6667dd', // Use item color or default
        isSelected,
        isFocused: false,
        canDelete: item.source === 'user', // User markers can be deleted
        // Empty timestamps array (not used for waveform - we use subSeries instead)
        timestamps: [],
        // Sub-series for Acceleration/Velocity (each is a separate waveform file)
        subSeries: [
          {
            id: `${item.id}-acceleration`,
            label: 'Acceleration',
            type: 'acceleration',
            enabled: enabledSeries.has('acceleration'),
            visible: isSelected && enabledSeries.has('acceleration'),
            color: ACCELERATION_COLOR,
          },
          {
            id: `${item.id}-velocity`,
            label: 'Velocity',
            type: 'velocity',
            enabled: enabledSeries.has('velocity'),
            visible: isSelected && enabledSeries.has('velocity'),
            color: VELOCITY_COLOR,
          },
        ],
      };
    });
  }, [expandableLegend, allTimestampItems, selectedItemIds, enabledSeries]);

  // Handle toggle series (acceleration/velocity visibility)
  const handleToggleSeries = useCallback((seriesId: string) => {
    setEnabledSeries((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(seriesId)) {
        // Don't allow disabling last series
        if (newSet.size > 1) {
          newSet.delete(seriesId);
        }
      } else {
        newSet.add(seriesId);
      }
      return newSet;
    });
  }, []);

  // Generate popoverTimestamps for the snapshots dropdown
  const popoverTimestamps = useMemo(() => {
    if (!isExpandableLegendEnabled() || !expandableLegend?.enabled) {
      return undefined;
    }
    return convertItemsToPopoverFormat(allTimestampItems, selectedTimestampsSet || new Set());
  }, [expandableLegend, allTimestampItems, selectedTimestampsSet]);

  // Handle toggle expandable series (sensor-level toggle)
  const handleToggleExpandableSeries = useCallback((seriesId: string) => {
    if (!setSelectedTimestampsSet) return;
    setSelectedTimestampsSet((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(seriesId)) {
        newSet.delete(seriesId);
      } else {
        newSet.add(seriesId);
      }
      return newSet;
    });
  }, [setSelectedTimestampsSet]);

  // Handle focus on expandable series
  const handleToggleExpandableSeriesFocus = useCallback((seriesId: string) => {
    // Toggle focus mode when clicking on a series
    if (legendSyncMode === 'focus') {
      // Already in focus mode - toggle this series
      handleToggleExpandableSeries(seriesId);
    } else {
      // Enter focus mode with this series
      setLegendSyncMode('focus');
      if (setSelectedTimestampsSet) {
        setSelectedTimestampsSet(new Set([seriesId]));
      }
    }
  }, [legendSyncMode, handleToggleExpandableSeries, setSelectedTimestampsSet]);

  // Handle removing a marker
  const handleRemoveMarker = useCallback((markerId: string) => {
    if (onRemoveSensorFusionMarker) {
      onRemoveSensorFusionMarker(markerId);
    }
    // Also remove from selected timestamps
    if (setSelectedTimestampsSet) {
      setSelectedTimestampsSet((prev) => {
        const newSet = new Set(prev);
        newSet.delete(markerId);
        return newSet;
      });
    }
  }, [onRemoveSensorFusionMarker, setSelectedTimestampsSet]);

  // Toggle expandable timestamp handler
  // seriesId is the timestamp ID (primary item), timestampId is the sub-series ID (acceleration/velocity)
  const handleToggleExpandableTimestamp = useCallback((seriesId: string, timestampId: string) => {
    // If timestampId contains '-acceleration' or '-velocity', it's a sub-series toggle
    if (timestampId && (timestampId.includes('-acceleration') || timestampId.includes('-velocity'))) {
      const seriesType = timestampId.includes('-acceleration') ? 'acceleration' : 'velocity';
      setEnabledSeries((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(seriesType)) {
          // Don't allow disabling the last series
          if (newSet.size > 1) {
            newSet.delete(seriesType);
          }
        } else {
          newSet.add(seriesType);
        }
        return newSet;
      });
    } else {
      // Otherwise, toggle the timestamp itself (primary item)
      setSelectedItemIds((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(seriesId)) {
          newSet.delete(seriesId);
        } else {
          newSet.add(seriesId);
        }
        return newSet;
      });
    }
  }, []);

  // Create data layers config
  const dataLayersConfig = useMemo(
    () =>
      createWaveformDataLayersConfig({
        activeDataLayers,
        baseTitle,
        from,
        to,
        waveformGraphs,
        waveformManager,
        enhancedQueryParams,
        forceDataRefresh,
        handleSensorLoadingStateChange,
        selectedComponentId,
        selectedComponentObj,
        legendSync,
        onToggleSensor: handleToggleSensor,
        registerSensorData,
        allEnabledSensors: registeredSensorData.enabledSensors,
        allSensorColors: registeredSensorData.sensorColors,
        masterSensorColorMap,
        selectedMarkerTimestamp,
        setSelectedMarkerTimestamp,
        selectedTimestampsSet,
        setSelectedTimestampsSet,
        debouncedSelectedTimestampsSet,
        verticalLinesState,
        sensorFusionVerticalLines,
        onRemoveSensorFusionMarker,
        onClearAllSensorFusionMarkers,
        focusedSensors,
        onFocus: handleFocus,
        focusMode,
        setFocusMode: (newFocusMode: boolean) => {
          if (newFocusMode) {
            setLegendSyncMode('focus');
          } else {
            setLegendSyncMode('multi');
          }
        },
        loadingSensors,
        setLoadingSensor: (sensorName: string) => {
          setLoadingSensors((prev) => new Set(prev).add(sensorName));
        },
        clearLoadingSensor: (sensorName: string) => {
          setLoadingSensors((prev) => {
            const newSet = new Set(prev);
            newSet.delete(sensorName);
            return newSet;
          });
        },
        setTriggerSensorReset,
        onToggleExpandableTimestamp: handleToggleExpandableTimestamp,
        onToggleExpandableSeries: handleToggleSeries, // For acceleration/velocity series toggle
        onToggleExpandableSeriesFocus: handleToggleExpandableSeriesFocus,
        enabledSeries,
        onRemoveMarker: handleRemoveMarker,
        config,
        allowMultiTimestampMode,
        expandableLegendSeries: waveformExpandableLegendSeries,
        popoverTimestamps,
      }),
    [
      activeDataLayers,
      baseTitle,
      from,
      to,
      waveformGraphs,
      waveformManager,
      enhancedQueryParams,
      forceDataRefresh,
      handleSensorLoadingStateChange,
      selectedComponentId,
      selectedComponentObj,
      legendSync,
      handleToggleSensor,
      registerSensorData,
      registeredSensorData.enabledSensors,
      registeredSensorData.sensorColors,
      masterSensorColorMap,
      selectedMarkerTimestamp,
      setSelectedMarkerTimestamp,
      selectedTimestampsSet,
      setSelectedTimestampsSet,
      debouncedSelectedTimestampsSet,
      verticalLinesState,
      sensorFusionVerticalLines,
      onRemoveSensorFusionMarker,
      onClearAllSensorFusionMarkers,
      focusedSensors,
      handleFocus,
      focusMode,
      loadingSensors,
      handleToggleExpandableTimestamp,
      handleToggleSeries,
      handleToggleExpandableSeriesFocus,
      handleRemoveMarker,
      enabledSeries,
      config,
      allowMultiTimestampMode,
      waveformExpandableLegendSeries,
      popoverTimestamps,
    ]
  );

  // Map configuration to component render map
  const dataLayersToComponent = dataLayersConfig.reduce((acc, layer) => {
    acc[layer.id] = layer.condition?.() ? layer.component() : null;
    return acc;
  }, {} as Record<string, React.ReactNode>);

  return (
    <div className="flex flex-col gap-4 w-full">
      {WAVEFORM_DATA_LAYERS_RENDER_ORDER.map((id) =>
        dataLayersToComponent[id] ? <React.Fragment key={id}>{dataLayersToComponent[id]}</React.Fragment> : null
      ).filter(Boolean)}
    </div>
  );
};

export default Waveform;
